import React, { useContext, useEffect, useState } from "react";

import { validateInput } from "../../../shared/validation";
import { checkMultiMatch } from "../typeahead/lib/check-match";
import { useAttributePairValidation } from "../../attributes/hooks/use-attribute-validation";
import { AttributesContext } from "../../../contexts/attributes";
import { useValidateAttributePair } from "../../../shared/hooks/use-validate-attribute-pair";

const DEFAULT_VALIDATION = {
    regEx: null,
    regExFailureMessage: null,
    maxLength: null,
    minLength: null,
    required: false,
};

const withValidate = (WrappedComponent) => (props) => {
    const attributes = useContext(AttributesContext);
    const { onRequireBy, validationDetails } = useAttributePairValidation();
    const validation = props.validation || { ...DEFAULT_VALIDATION };
    const validatePair = useValidateAttributePair();
    const [state, setState] = useState({
        isValid: true,
        isInvalidPair: false,
        errorMsg: [],
    });

    useEffect(() => {
        if (!validationDetails) {
            return;
        }

        const { data: attributeValues } = attributes;
        const { uuid } = props.attribute;
        const [validatedAttribute] = validationDetails;

        if (validation.required) {
            const validate = validateInput(props.defaultValue, validation);

            if (state.isValid !== validate.isValid || !checkMultiMatch(state.errorMsg, validate.errorMsg)) {
                return setState({
                    isValid: validate.isValid,
                    errorMsg: validate.errorMsg,
                });
            }
        }

        if (validatedAttribute) {
            if (validatedAttribute.pairedUuid === uuid) {
                const isInvalid = validatePair({ value: validatedAttribute.value }, { value: attributeValues[validatedAttribute.uuid] });

                return setState({
                    isValid: !isInvalid,
                    errorMsg: isInvalid ? ["Field is required"] : [],
                });
            }

            if (validatedAttribute.uuid === uuid) {
                const isInvalid = validatePair({ value: attributeValues[validatedAttribute.uuid] }, { value: validatedAttribute.value });

                return setState({
                    isValid: !isInvalid,
                    isInvalidPair: isInvalid,
                    errorMsg: isInvalid ? ["Field is required"] : [],
                });
            }
        }
    }, [validationDetails, attributes, props.attribute, props.defaultValue, validatePair]);

    useEffect(() => {
        if (validationDetails) {
            return;
        }

        const validate = validateInput(props.defaultValue, validation);

        if (state.isValid !== validate.isValid || !checkMultiMatch(state.errorMsg, validate.errorMsg)) {
            setState({
                isValid: validate.isValid,
                errorMsg: validate.errorMsg,
            });
        }
    }, [props.defaultValue, state.errorMsg, state.isValid, validation, validationDetails]);

    const transformValue = (value) => {
        if (!validation || !validation.textTransform || typeof value !== "string") {
            return value;
        }

        let newValue;
        switch (validation.textTransform.toLowerCase()) {
            case "uppercase":
                newValue = value.toUpperCase();
                break;
            case "lowercase":
                newValue = value.toLowerCase();
                break;
            default:
                newValue = value;
        }

        return newValue;
    };

    const handlePairValidation = (value) => {
        if (!props.attribute) {
            return;
        }

        const { pairedAttributes, uuid } = props.attribute;

        if (!pairedAttributes) {
            return;
        }

        const [pairedAttribute] = pairedAttributes;

        if (pairedAttribute && pairedAttribute.required && onRequireBy !== undefined) {
            onRequireBy(pairedAttribute.uuid, uuid, value);
        }
    };

    const updateOnBlur = (name, value) => {
        if (state.isInvalidPair) {
            return;
        }

        const newValue = transformValue(value);
        const validate = validateInput(newValue, validation);

        setState({
            errorMsg: validate.errorMsg,
            isValid: validate.isValid,
        });

        if (!validate.isValid && validate.errorMsg.length > 0) {
            return;
        }

        if (props.updateOnBlur) {
            props.updateOnBlur(name, newValue);
        }
    };

    const updateOnChange = (name, value) => {
        const newValue = transformValue(value);
        const validate = validateInput(newValue, validation);

        setState({
            errorMsg: validate.errorMsg,
            isValid: validate.isValid,
        });

        if (props.updateOnChange) {
            props.updateOnChange(name, newValue);
        }
    };

    const errorClass = !state.isValid ? "-error" : "";
    const errorMsg = !state.isValid ? <div className="error-message">{state.errorMsg.join(", ")}</div> : null;

    return (
        <div className={`validation-wrapper ${errorClass}`}>
            <WrappedComponent
                {...props}
                updateOnChange={props.updateOnChange ? updateOnChange : null}
                updateOnBlur={props.updateOnBlur ? updateOnBlur : null}
                onValidatePair={handlePairValidation}
                pattern={validation.regEx}
                maxLength={validation.max}
                minLength={validation.min}
            />
            {errorMsg}
        </div>
    );
};

export default withValidate;
